import { Component, Input, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Dynamic Value Input Component
 * Renders appropriate input control based on data type:
 * - Integer (Int, int): number input
 * - String (string, String): text input
 * - Boolean (Bool): radio buttons (True/False)
 * - Comma-separated defaultValue: dropdown select (only in 'value' mode)
 * 
 * Modes:
 * - 'value' (default): Used for entering actual values, shows dropdown for comma-separated defaultValue
 * - 'input': Used for entering definitions/settings, always shows input (no dropdown for comma-separated values)
 */
@Component({
  selector: 'app-dynamic-value-input',
  templateUrl: './dynamic-value-input.component.html',
  styleUrls: ['./dynamic-value-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DynamicValueInputComponent),
      multi: true
    }
  ]
})
export class DynamicValueInputComponent implements ControlValueAccessor {
  @Input() dataType: string;
  @Input() defaultValue: string;
  @Input() placeholder: string = 'Enter value';
  @Input() disabled: boolean = false;
  @Input() mode: 'value' | 'input' = 'value'; // 'value' for actual values, 'input' for definitions
  
  private _value: any;
  
  get value(): any {
    return this._value;
  }
  
  set value(val: any) {
    this._value = val;
    this.onChange(val);
    this.onTouched();
  }

  onChange: any = () => {};
  onTouched: any = () => {};

  writeValue(value: any): void {
    this._value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * Check if input should be a number input
   */
  isIntegerInput(): boolean {
    return this.dataType && 
           !this.hasCommaSeparatedValues() &&
           (this.dataType === 'Int' || this.dataType === 'int' || this.dataType === 'integer' || this.dataType === 'Integer');
  }

  /**
   * Check if input should be a text input
   * In 'input' mode, always show text input for string types (even with comma-separated values)
   * In 'value' mode, only show text input if no comma-separated values
   */
  isStringInput(): boolean {
    if (!this.dataType || this.isInvisible()) {
      return false;
    }
    
    const isStringType = this.dataType === 'string' || this.dataType === 'String';
    
    if (this.mode === 'input') {
      // In input mode, always show text input for string types
      return isStringType;
    } else {
      // In value mode, only show text input if no comma-separated values
      return isStringType && !this.hasCommaSeparatedValues();
    }
  }

  /**
   * Check if input should be boolean radio buttons
   */
  isBooleanInput(): boolean {
    return this.dataType && (this.dataType === 'Bool' || this.dataType === 'bool' || this.dataType === 'Boolean' || this.dataType === 'boolean');
  }

  /**
   * Check if input should be invisible (hidden)
   */
  isInvisible(): boolean {
    return this.dataType && (this.dataType === 'invisible' || this.dataType === 'Invisible');
  }

  /**
   * Check if input should be a dropdown
   * Only show dropdown in 'value' mode, not in 'input' mode
   */
  isDropdownInput(): boolean {
    return this.mode === 'value' && this.hasCommaSeparatedValues();
  }

  /**
   * Check if defaultValue contains comma-separated values
   */
  private hasCommaSeparatedValues(): boolean {
    return this.defaultValue && this.defaultValue.indexOf(',') > -1;
  }

  /**
   * Get dropdown options from comma-separated defaultValue
   */
  getDropdownOptions(): string[] {
    if (!this.hasCommaSeparatedValues()) {
      return [];
    }
    return this.defaultValue.split(',').map(item => item.trim());
  }

  /**
   * Set boolean value to True
   */
  setValueToTrue(): void {
    this.value = 'True';
  }

  /**
   * Set boolean value to False
   */
  setValueToFalse(): void {
    this.value = 'False';
  }

  /**
   * Check if current value is True
   */
  isValueTrue(): boolean {
    return this._value === 'True' || this._value === 'true';
  }

  /**
   * Check if current value is False
   */
  isValueFalse(): boolean {
    return this._value === 'False' || this._value === 'false';
  }

  /**
   * Get placeholder text based on data type
   */
  getPlaceholder(): string {
    if (this.placeholder && this.placeholder !== 'Enter value') {
      return this.placeholder;
    }

    if (this.isIntegerInput()) {
      return 'Please enter new value (Only Numeric)';
    } else if (this.isStringInput()) {
      return 'Please enter new value (Only Text)';
    } else if (this.isDropdownInput()) {
      return 'Please Select';
    }
    
    return 'Enter value';
  }
}

